/**
 * Vegetation — layered procedural trees + grass tufts.
 *
 * Tree species:
 *   - Pine: three stacked cones of decreasing radius on a tall slim trunk
 *   - Oak: large sphere canopy on a thick trunk, with a smaller secondary blob
 *   - Birch: slim white trunk + sparse spherical canopy
 *   - Willow: squat trunk + drooping ellipsoid canopy
 *
 * Each species uses a per-instance colored InstancedMesh so we can vary
 * foliage tint across the scatter without per-tree draw calls.
 *
 * Grass tufts are low-poly three-blade billboards instanced across the
 * audience fields for ground detail.
 */

import * as THREE from 'three';
import type { Terrain } from '@/world/Terrain';
import { mulberry32 } from '@/core/MathUtils';

export interface ScatterRule {
  exclude: { x: number; z: number; r: number }[];
  count: number;
  size: number;
}

const PINE_TRUNK_COLORS = [0x3a2814, 0x4a3020, 0x2e1e10];
const PINE_LEAF_COLORS = [0x264a1a, 0x2e5820, 0x204218, 0x386928];
const OAK_LEAF_COLORS = [0x3c6028, 0x4a7030, 0x3a5e26, 0x5a8038];
const BIRCH_LEAF_COLORS = [0x88aa48, 0x98b858, 0x78a040];
const WILLOW_LEAF_COLORS = [0x4a6a2a, 0x5a7a36, 0x406028];

export class Vegetation {
  static build(terrain: Terrain, rule: ScatterRule, seed = 1337): THREE.Group {
    const group = new THREE.Group();
    group.name = 'Vegetation';

    const rand = mulberry32(seed);
    const maxCount = rule.count;

    // ─── Pine trees: three stacked cones + slim trunk ───
    const pineCount = Math.ceil(maxCount * 0.35);
    const pineTrunkGeo = new THREE.CylinderGeometry(0.15, 0.28, 4.5, 6);
    const pineTrunkMat = new THREE.MeshStandardMaterial({
      vertexColors: false,
      roughness: 0.9
    });
    const pineTrunk = new THREE.InstancedMesh(pineTrunkGeo, pineTrunkMat, pineCount);
    pineTrunk.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(pineCount * 3),
      3
    );
    pineTrunk.castShadow = true;

    const pineLayer1Geo = new THREE.ConeGeometry(1.9, 2.6, 8);
    const pineLayer2Geo = new THREE.ConeGeometry(1.5, 2.4, 8);
    const pineLayer3Geo = new THREE.ConeGeometry(1.0, 2.0, 8);
    const pineLeafMat = new THREE.MeshStandardMaterial({
      vertexColors: false,
      roughness: 0.85
    });
    const pineLayer1 = new THREE.InstancedMesh(pineLayer1Geo, pineLeafMat, pineCount);
    const pineLayer2 = new THREE.InstancedMesh(pineLayer2Geo, pineLeafMat, pineCount);
    const pineLayer3 = new THREE.InstancedMesh(pineLayer3Geo, pineLeafMat, pineCount);
    pineLayer1.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(pineCount * 3), 3);
    pineLayer2.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(pineCount * 3), 3);
    pineLayer3.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(pineCount * 3), 3);
    pineLayer1.castShadow = true;
    pineLayer2.castShadow = true;
    pineLayer3.castShadow = true;

    // ─── Oak trees: big sphere + secondary blob + thick trunk ───
    const oakCount = Math.ceil(maxCount * 0.3);
    const oakTrunkGeo = new THREE.CylinderGeometry(0.35, 0.5, 3.8, 8);
    const oakTrunkMat = new THREE.MeshStandardMaterial({ vertexColors: false, roughness: 0.9 });
    const oakTrunk = new THREE.InstancedMesh(oakTrunkGeo, oakTrunkMat, oakCount);
    oakTrunk.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(oakCount * 3), 3);
    oakTrunk.castShadow = true;

    const oakCanopyGeo = new THREE.SphereGeometry(2.6, 10, 8);
    const oakLeafMat = new THREE.MeshStandardMaterial({ vertexColors: false, roughness: 0.8 });
    const oakCanopy = new THREE.InstancedMesh(oakCanopyGeo, oakLeafMat, oakCount);
    oakCanopy.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(oakCount * 3), 3);
    oakCanopy.castShadow = true;

    const oakCanopy2Geo = new THREE.SphereGeometry(1.7, 8, 6);
    const oakCanopy2 = new THREE.InstancedMesh(oakCanopy2Geo, oakLeafMat, oakCount);
    oakCanopy2.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(oakCount * 3), 3);
    oakCanopy2.castShadow = true;

    // ─── Birch trees: slim white trunk + sparse canopy ───
    const birchCount = Math.ceil(maxCount * 0.2);
    const birchTrunkGeo = new THREE.CylinderGeometry(0.12, 0.17, 5.2, 6);
    const birchTrunkMat = new THREE.MeshStandardMaterial({
      color: 0xe8e4dc,
      roughness: 0.75
    });
    const birchTrunk = new THREE.InstancedMesh(birchTrunkGeo, birchTrunkMat, birchCount);
    birchTrunk.castShadow = true;

    const birchCanopyGeo = new THREE.SphereGeometry(1.6, 8, 6);
    const birchLeafMat = new THREE.MeshStandardMaterial({ vertexColors: false, roughness: 0.8 });
    const birchCanopy = new THREE.InstancedMesh(birchCanopyGeo, birchLeafMat, birchCount);
    birchCanopy.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(birchCount * 3), 3);
    birchCanopy.castShadow = true;

    // ─── Willow trees: squat + drooping ellipsoid ───
    const willowCount = Math.ceil(maxCount * 0.15);
    const willowTrunkGeo = new THREE.CylinderGeometry(0.4, 0.6, 2.6, 8);
    const willowTrunkMat = new THREE.MeshStandardMaterial({ color: 0x3a2814, roughness: 0.9 });
    const willowTrunk = new THREE.InstancedMesh(willowTrunkGeo, willowTrunkMat, willowCount);
    willowTrunk.castShadow = true;

    const willowCanopyGeo = new THREE.SphereGeometry(2.4, 10, 8);
    // Scale into an ellipsoid droop via per-instance matrix
    const willowLeafMat = new THREE.MeshStandardMaterial({ vertexColors: false, roughness: 0.8 });
    const willowCanopy = new THREE.InstancedMesh(willowCanopyGeo, willowLeafMat, willowCount);
    willowCanopy.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(willowCount * 3), 3);
    willowCanopy.castShadow = true;

    // ─── Scatter placement ───
    const m4 = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const rot = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    const col = new THREE.Color();
    const half = rule.size / 2;

    let iPine = 0, iOak = 0, iBirch = 0, iWillow = 0;
    let placed = 0;
    let attempts = 0;
    const maxAttempts = maxCount * 15;

    while (placed < maxCount && attempts < maxAttempts) {
      attempts++;
      const x = (rand() - 0.5) * rule.size;
      const z = (rand() - 0.5) * rule.size;
      if (Math.abs(x) > half || Math.abs(z) > half) continue;
      let inside = false;
      for (const ex of rule.exclude) {
        const dx = x - ex.x;
        const dz = z - ex.z;
        if (dx * dx + dz * dz < ex.r * ex.r) {
          inside = true;
          break;
        }
      }
      if (inside) continue;
      // Reject steep slopes
      const y = terrain.heightAt(x, z);
      const yL = terrain.heightAt(x - 2, z);
      const yR = terrain.heightAt(x + 2, z);
      if (Math.abs(yL - yR) > 5) continue;

      const pick = rand();
      const scale = 0.78 + rand() * 0.45;
      const yaw = rand() * Math.PI * 2;
      rot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
      scl.set(scale, scale, scale);

      if (pick < 0.35 && iPine < pineCount) {
        // ── Pine ──
        const trunkH = 4.5 * scale;
        pos.set(x, y + trunkH / 2, z);
        m4.compose(pos, rot, scl);
        pineTrunk.setMatrixAt(iPine, m4);
        col.setHex(PINE_TRUNK_COLORS[Math.floor(rand() * PINE_TRUNK_COLORS.length)]);
        pineTrunk.setColorAt(iPine, col);

        const leafColor = PINE_LEAF_COLORS[Math.floor(rand() * PINE_LEAF_COLORS.length)];
        col.setHex(leafColor);
        // Three cone layers, top to bottom
        pos.set(x, y + trunkH + 3.4 * scale, z);
        m4.compose(pos, rot, scl);
        pineLayer3.setMatrixAt(iPine, m4);
        pineLayer3.setColorAt(iPine, col);

        pos.set(x, y + trunkH + 1.8 * scale, z);
        m4.compose(pos, rot, scl);
        pineLayer2.setMatrixAt(iPine, m4);
        pineLayer2.setColorAt(iPine, col);

        pos.set(x, y + trunkH + 0.4 * scale, z);
        m4.compose(pos, rot, scl);
        pineLayer1.setMatrixAt(iPine, m4);
        pineLayer1.setColorAt(iPine, col);
        iPine++;
      } else if (pick < 0.65 && iOak < oakCount) {
        // ── Oak ──
        const trunkH = 3.8 * scale;
        pos.set(x, y + trunkH / 2, z);
        m4.compose(pos, rot, scl);
        oakTrunk.setMatrixAt(iOak, m4);
        col.setHex(PINE_TRUNK_COLORS[Math.floor(rand() * PINE_TRUNK_COLORS.length)]);
        oakTrunk.setColorAt(iOak, col);

        const leafHex = OAK_LEAF_COLORS[Math.floor(rand() * OAK_LEAF_COLORS.length)];
        col.setHex(leafHex);
        pos.set(x, y + trunkH + 1.8 * scale, z);
        m4.compose(pos, rot, scl);
        oakCanopy.setMatrixAt(iOak, m4);
        oakCanopy.setColorAt(iOak, col);

        pos.set(
          x + (rand() - 0.5) * 1.2 * scale,
          y + trunkH + 2.8 * scale,
          z + (rand() - 0.5) * 1.2 * scale
        );
        m4.compose(pos, rot, scl);
        oakCanopy2.setMatrixAt(iOak, m4);
        oakCanopy2.setColorAt(iOak, col);
        iOak++;
      } else if (pick < 0.85 && iBirch < birchCount) {
        // ── Birch ──
        const trunkH = 5.2 * scale;
        pos.set(x, y + trunkH / 2, z);
        m4.compose(pos, rot, scl);
        birchTrunk.setMatrixAt(iBirch, m4);

        const leafHex = BIRCH_LEAF_COLORS[Math.floor(rand() * BIRCH_LEAF_COLORS.length)];
        col.setHex(leafHex);
        pos.set(x, y + trunkH + 0.6 * scale, z);
        m4.compose(pos, rot, scl);
        birchCanopy.setMatrixAt(iBirch, m4);
        birchCanopy.setColorAt(iBirch, col);
        iBirch++;
      } else if (iWillow < willowCount) {
        // ── Willow ──
        const trunkH = 2.6 * scale;
        pos.set(x, y + trunkH / 2, z);
        m4.compose(pos, rot, scl);
        willowTrunk.setMatrixAt(iWillow, m4);

        const leafHex = WILLOW_LEAF_COLORS[Math.floor(rand() * WILLOW_LEAF_COLORS.length)];
        col.setHex(leafHex);
        // Ellipsoid squash
        pos.set(x, y + trunkH + 2.2 * scale, z);
        scl.set(scale * 1.2, scale * 0.85, scale * 1.2);
        m4.compose(pos, rot, scl);
        scl.set(scale, scale, scale);
        willowCanopy.setMatrixAt(iWillow, m4);
        willowCanopy.setColorAt(iWillow, col);
        iWillow++;
      }
      placed++;
    }

    pineTrunk.count = iPine;
    pineLayer1.count = iPine;
    pineLayer2.count = iPine;
    pineLayer3.count = iPine;
    oakTrunk.count = iOak;
    oakCanopy.count = iOak;
    oakCanopy2.count = iOak;
    birchTrunk.count = iBirch;
    birchCanopy.count = iBirch;
    willowTrunk.count = iWillow;
    willowCanopy.count = iWillow;

    for (const im of [pineTrunk, pineLayer1, pineLayer2, pineLayer3, oakTrunk, oakCanopy, oakCanopy2, birchTrunk, birchCanopy, willowTrunk, willowCanopy]) {
      im.instanceMatrix.needsUpdate = true;
      if (im.instanceColor) im.instanceColor.needsUpdate = true;
    }

    group.add(
      pineTrunk, pineLayer1, pineLayer2, pineLayer3,
      oakTrunk, oakCanopy, oakCanopy2,
      birchTrunk, birchCanopy,
      willowTrunk, willowCanopy
    );
    return group;
  }

  /**
   * Grass tufts scattered near the ground across audience zones.
   */
  static buildGrassTufts(
    terrain: Terrain,
    centers: { x: number; z: number; r: number; count: number }[],
    seed = 6969
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = 'GrassTufts';

    const total = centers.reduce((s, c) => s + c.count, 0);
    if (total === 0) return group;

    // Single triangle-fan geometry that reads as a grass clump
    const vertices: number[] = [];
    for (let b = 0; b < 5; b++) {
      const a = (b / 5) * Math.PI * 2;
      const dx = Math.cos(a) * 0.08;
      const dz = Math.sin(a) * 0.08;
      const h = 0.25 + (b % 2) * 0.05;
      vertices.push(dx, 0, dz, -dx, 0, -dz, 0, h, 0);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({
      color: 0x6aa42a,
      vertexColors: false,
      roughness: 0.9,
      side: THREE.DoubleSide
    });

    const inst = new THREE.InstancedMesh(geo, mat, total);
    inst.castShadow = false;
    inst.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(total * 3), 3);

    const m4 = new THREE.Matrix4();
    const pos = new THREE.Vector3();
    const rot = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    const col = new THREE.Color();
    const rand = mulberry32(seed);
    let i = 0;
    for (const c of centers) {
      let placed = 0;
      let attempts = 0;
      while (placed < c.count && attempts < c.count * 6) {
        attempts++;
        const a = rand() * Math.PI * 2;
        const r = Math.sqrt(rand()) * c.r;
        const x = c.x + Math.cos(a) * r;
        const z = c.z + Math.sin(a) * r;
        const y = terrain.heightAt(x, z);
        const scale = 0.8 + rand() * 1.2;
        rot.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rand() * Math.PI * 2);
        scl.set(scale, scale, scale);
        pos.set(x, y, z);
        m4.compose(pos, rot, scl);
        inst.setMatrixAt(i, m4);
        // Varied greens
        const h = 0.25 + rand() * 0.1;
        const s = 0.55 + rand() * 0.3;
        const l = 0.28 + rand() * 0.15;
        col.setHSL(h, s, l);
        inst.setColorAt(i, col);
        i++;
        placed++;
      }
    }
    inst.count = i;
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    group.add(inst);
    return group;
  }
}
